import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Field, StatusBadge } from '../components/ui.jsx';
import { api, ApiError } from '../api/client.js';
import { useTitle } from '../lib/hooks.js';
import { formatDate } from '../lib/format.js';

/**
 * Checking on an application without signing in.
 *
 * Somebody who applied as a guest has no account until a landlord approves them,
 * so this is the only way back to their application. The reference alone is not
 * enough: it has to be quoted with the email they applied under, which is why a
 * guessed reference gets nobody anywhere.
 */

const WHAT_HAPPENS_NEXT = {
  submitted: 'The landlord has it and has not opened it yet. Most reply within a few days.',
  under_review:
    'The landlord is looking at your application. They may message you or ask to show you the house.',
  approved:
    'You have been approved. Check your email for your username and temporary password, then sign in to go on with the lease.',
  rejected: 'The landlord has turned this one down. There are other houses on the site.',
  withdrawn: 'This application was withdrawn.'
};

const TrackApplication = () => {
  useTitle('Track an application');
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    reference: params.get('reference') || '',
    email: params.get('email') || ''
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setErrors({});
    setResult(null);

    try {
      setResult(
        await api.post('/applications/track', {
          reference: form.reference.trim(),
          email: form.email.trim()
        })
      );
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.details) setErrors(requestError.details);
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page container">
      <div className="auth-card">
        <h1>Track an application</h1>
        <p className="muted">
          Applied without an account? Enter the reference you were given and the email you applied
          with.
        </p>

        {error ? <div className="notice notice-bad">{error}</div> : null}

        <form className="stack" onSubmit={submit}>
          <Field label="Reference" hint="It looks like APP-2026-4F7KQR." error={errors.reference} required>
            <input
              type="text"
              required
              value={form.reference}
              onChange={(event) => set({ reference: event.target.value })}
              placeholder="APP-2026-4F7KQR"
              autoComplete="off"
            />
          </Field>

          <Field label="Email you applied with" error={errors.email} required>
            <input
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(event) => set({ email: event.target.value })}
            />
          </Field>

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? 'Checking...' : 'Check status'}
          </button>
        </form>

        {result ? (
          <div className="track-result">
            <header className="track-result-head">
              <div>
                <strong>{result.property.title}</strong>
                <span className="muted">{result.property.city}</span>
              </div>
              <StatusBadge status={result.status} />
            </header>

            <dl className="summary-list">
              <div>
                <dt>Reference</dt>
                <dd>{result.reference}</dd>
              </div>
              <div>
                <dt>Applied</dt>
                <dd>{formatDate(result.appliedOn)}</dd>
              </div>
              {result.decidedOn ? (
                <div>
                  <dt>Decided</dt>
                  <dd>{formatDate(result.decidedOn)}</dd>
                </div>
              ) : null}
            </dl>

            <p>{WHAT_HAPPENS_NEXT[result.status] || 'The landlord will be in touch.'}</p>

            {result.note ? (
              <p className="decision-note">
                <strong>Note from the landlord:</strong> {result.note}
              </p>
            ) : null}

            {result.canSignIn ? (
              <Link to="/login" className="btn btn-primary btn-block">
                Sign in to your account
              </Link>
            ) : null}
          </div>
        ) : null}

        <p className="auth-alt">
          Looking for a house? <Link to="/search">Browse listings</Link>
        </p>
      </div>

      <aside className="auth-aside">
        <h2>Applying without an account</h2>
        <ul className="tick-list">
          <li>Apply for any house on the site without registering first</li>
          <li>Keep the reference you are given and check back here any time</li>
          <li>Nothing to remember: your email is your username if you are approved</li>
          <li>Approved applicants get a temporary password by email</li>
          <li>From then on you sign the lease, pay rent and print receipts in one place</li>
        </ul>
      </aside>
    </div>
  );
};

export default TrackApplication;
